import { createContext, useContext } from "react";
import { ReadUser, UpdateUser, CreateUser } from "@/types/user";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/services/api";

export type UserHandlerKey = "activate" | "deactivate" | "update" | "create";

export type UserLoadingReason =
  | "fetching_user"
  | "updating_user"
  | "creating_user"
  | "activating_user"
  | "deactivating_user"
  | "unknown";

export type UserLoadingState =
  | { isLoading: false }
  | { isLoading: true; reason: UserLoadingReason };

export interface MutationsObject {
  activateUserMutation: UseMutationResult<
    ReadUser,
    ApiError,
    undefined,
    unknown
  >;
  deactivateUserMutation: UseMutationResult<
    ReadUser,
    ApiError,
    undefined,
    unknown
  >;
  updateUserMutation: UseMutationResult<
    ReadUser,
    ApiError,
    UpdateUser,
    unknown
  >;
  createUserMutation: UseMutationResult<
    ReadUser,
    ApiError,
    CreateUser,
    unknown
  >;
}

export interface QueriesObject {
  userQuery: UseQueryResult<ReadUser, Error>;
}

export interface UsersContextType {
  query: UseQueryResult<ReadUser, Error>;
  data?: ReadUser;
  errorState: { error: Error | null; isQuery: boolean };
  loadingState: UserLoadingState;
  clearError: () => void;
  refreshUser: () => void;
  handleActivate: () => void;
  handleDeactivate: () => void;
  handleUpdate: (updateData: UpdateUser) => Promise<ReadUser>;
  handleCreate: (createData: CreateUser) => Promise<ReadUser>;
}

const UsersContext = createContext<UsersContextType>({} as UsersContextType);

export const useUsersContext = () => {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error("useUsersContext must be used within UsersProvider");
  }
  return context;
};

export { UsersContext };
